<div class="quarterly-review">
  <div class="header-row">
    <div>
      <div class="mat-h3">Overdraft Quarterly Review</div>
      <div class="subtitle">Review checkpoint progress and retry unfinished work.</div>
    </div>
    <div class="header-actions">
      <button mat-stroked-button color="primary" [disabled]="isLoadingRuns || isRetrying" (click)="loadRuns()">
        <span class="mdi mdi-refresh"></span>
        Refresh
      </button>
      <button mat-flat-button color="primary" [disabled]="!canRetryRun" (click)="retryRun()">
        Retry Unfinished Accounts
      </button>
    </div>
  </div>

  @if (isLoadingRuns) {
    <custom-content-loader type="list"></custom-content-loader>
  } @else if (!runs.length) {
    <div class="empty-state">No overdraft quarterly review runs are available.</div>
  } @else {
    <div class="run-selector">
      <mat-form-field appearance="outline">
        <mat-label>Review quarter</mat-label>
        <mat-select [value]="selectedRunKey" (selectionChange)="onRunChange($event.value)">
          @for (run of runs; track run.run.id) {
            <mat-option [value]="run.run.reviewYear + '-' + run.run.reviewQuarter">
              {{run.run.reviewYear}} Q{{run.run.reviewQuarter}} — {{formatLabel(run.run.status)}}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
      @if (selectedRun) {
        <div class="run-meta">
          Revision {{selectedRun.run.revision}} · Last updated {{formatDate(selectedRun.run.lastUpdate)}}
        </div>
      }
    </div>

    @if (selectedRun) {
      <div class="summary-grid">
        <div class="summary-card">
          <span class="summary-label">Total</span>
          <span class="summary-value">{{selectedRun.totalAccounts}}</span>
        </div>
        <div class="summary-card">
          <span class="summary-label">Completed</span>
          <span class="summary-value status-completed">{{selectedRun.completedAccounts}}</span>
        </div>
        <div class="summary-card">
          <span class="summary-label">Failed</span>
          <span class="summary-value status-failed">{{selectedRun.failedAccounts}}</span>
        </div>
        <div class="summary-card">
          <span class="summary-label">Pending</span>
          <span class="summary-value status-pending">{{selectedRun.pendingAccounts}}</span>
        </div>
        <div class="summary-card">
          <span class="summary-label">Processing</span>
          <span class="summary-value status-processing">{{selectedRun.processingAccounts}}</span>
        </div>
        <div class="summary-card">
          <span class="summary-label">Skipped</span>
          <span class="summary-value status-skipped">{{selectedRun.skippedAccounts}}</span>
        </div>
      </div>

      <div class="filter-row">
        <mat-form-field class="account-search">
          <input matInput type="text" inputmode="numeric" pattern="[0-9]*"
            placeholder="Search account ID" [(ngModel)]="accountIdFilter">
          <span class="mdi mdi-magnify" matPrefix></span>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Status</mat-label>
          <mat-select [(ngModel)]="statusFilter">
            @for (option of statusOptions; track option.value) {
              <mat-option [value]="option.value">{{option.label}}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        <div class="filter-actions">
          <button mat-flat-button color="primary" (click)="applyFilters()">Apply Filters</button>
          @if (hasFilters) {
            <button mat-stroked-button color="primary" (click)="clearFilters()">Clear</button>
          }
        </div>
      </div>

      @if (isLoadingAccounts) {
        <custom-content-loader type="list"></custom-content-loader>
      }
      <div [ngClass]="{hidden: isLoadingAccounts, 'table-section': true}">
        <table mat-table [dataSource]="dataSource" multiTemplateDataRows matSort
          [matSortActive]="sortActive" [matSortDirection]="sortDirection"
          (matSortChange)="onSortData($event)">
          <ng-container matColumnDef="pismoAccountId">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Account ID</th>
            <td mat-cell *matCellDef="let account">{{account.pismoAccountId}}</td>
          </ng-container>

          <ng-container matColumnDef="accountName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Account Name</th>
            <td mat-cell *matCellDef="let account" class="name-cell"
              [matTooltip]="account.accountName || '-'">{{account.accountName || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
            <td mat-cell *matCellDef="let account">
              <span [ngClass]="getStatusClass(account.status)">{{formatLabel(account.status)}}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="currentStep">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Current Step</th>
            <td mat-cell *matCellDef="let account">{{formatLabel(account.currentStep)}}</td>
          </ng-container>

          <ng-container matColumnDef="stepAttemptCount">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Attempts</th>
            <td mat-cell *matCellDef="let account">{{account.stepAttemptCount}} / {{account.maxAttempts}}</td>
          </ng-container>

          <ng-container matColumnDef="bankStatementStatus">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Bank Statements</th>
            <td mat-cell *matCellDef="let account">{{formatLabel(account.bankStatementStatus)}}</td>
          </ng-container>

          <ng-container matColumnDef="lastUpdate">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Last Updated</th>
            <td mat-cell *matCellDef="let account">{{formatDate(account.lastUpdate)}}</td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let account">
              @if (canRetryAccount(account)) {
                <button mat-stroked-button color="primary" (click)="$event.stopPropagation(); retryAccount(account)">
                  Retry
                </button>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="expandedDetail">
            <td mat-cell *matCellDef="let account" class="expanded-row" [attr.colspan]="displayedColumns.length">
              <div class="account-detail"
                [@detailExpand]="account.id === expandedAccountId ? 'expanded' : 'collapsed'">
                <div class="detail-grid">
                  <div><strong>Pismo status</strong><span>{{account.pismoAccountStatus || '-'}}</span></div>
                  <div><strong>Opportunity ID</strong><span>{{account.opportunityId || '-'}}</span></div>
                  <div><strong>Salesforce account</strong><span>{{account.salesforceAccountId || '-'}}</span></div>
                  <div><strong>ABN</strong><span>{{account.abn || '-'}}</span></div>
                  <div><strong>Current balance</strong><span>{{formatAmount(account.currentBalance)}}</span></div>
                  <div><strong>Account limit</strong><span>{{formatAmount(account.accountLimit)}}</span></div>
                  <div><strong>Facility utilisation trigger</strong><span>{{account.triggerFacilityUtilisation ? 'Yes' : 'No'}}</span></div>
                  <div><strong>Dishonour trigger</strong><span>{{account.triggerDishonour ? 'Yes' : 'No'}}</span></div>
                  <div><strong>Arrears trigger</strong><span>{{account.triggerArrears ? 'Yes' : 'No'}}</span></div>
                  <div><strong>Triggered</strong><span>{{account.isTriggered ? 'Yes' : 'No'}}</span></div>
                  <div><strong>Bank statement end</strong><span>{{formatDate(account.bankStatementEndDate)}}</span></div>
                  <div><strong>Customer email</strong><span>{{formatLabel(account.customerEmailStatus)}}</span></div>
                  <div><strong>Revenue month 1</strong><span>{{formatAmount(account.revenue0)}}</span></div>
                  <div><strong>Revenue month 2</strong><span>{{formatAmount(account.revenue1)}}</span></div>
                  <div><strong>Revenue month 3</strong><span>{{formatAmount(account.revenue2)}}</span></div>
                  <div><strong>Previous average revenue</strong><span>{{formatAmount(account.previousAvgRevenues)}}</span></div>
                  <div class="wide"><strong>New loan parties</strong><span>{{formatStoredArray(account.newLoanPaymentPartiesJson)}}</span></div>
                  <div class="wide"><strong>Dishonour transactions</strong><span>{{formatStoredArray(account.dishonourTransactionsJson)}}</span></div>
                  <div><strong>Processing started</strong><span>{{formatDate(account.processingStartedAt)}}</span></div>
                  <div><strong>Completed</strong><span>{{formatDate(account.completedAt)}}</span></div>
                  @if (account.lastError) {
                    <div class="wide error-detail"><strong>Last error</strong><span>{{account.lastError}}</span></div>
                  }
                </div>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let account; columns: displayedColumns" class="account-row"
            [class.expanded]="account.id === expandedAccountId"
            (click)="expandedAccountId = account.id === expandedAccountId ? null : account.id"></tr>
          <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row"></tr>
        </table>
        @if (!dataSource.length) {
          <div class="empty-state">No accounts match the selected filters.</div>
        }
        @if (total) {
          <app-custom-paginator [length]="total" [pageIndex]="offset" [pageSize]="limit"
            [pageSizeOptions]="[10, 20, 50]" (page)="onPagination($event)"></app-custom-paginator>
        }
      </div>
    }
  }
</div>
